import React, { useEffect, useMemo, useRef, useState } from "react";
import { formatRoleLabel } from "../utils/roleLabels";
import "../styles/RoleSelect.css";

export type RoleOption = { id: number; name: string };

type Props = {
    options: RoleOption[];
    valueId?: number | null;
    valueName?: string | null;
    disabled?: boolean;
    placeholder?: string;
    onChange: (opt: RoleOption) => void;
};

function badgeClass(name?: string): string {
    const k = (name || "").toLowerCase();
    if (k === "admin" || k === "administrator") return "rs-badge rs-badge--admin";
    if (k === "staff") return "rs-badge rs-badge--staff";
    if (k === "coach") return "rs-badge rs-badge--coach";
    if (k === "athlete") return "rs-badge rs-badge--athlete";
    return "rs-badge";
}

export default function RoleSelect({
                                       options,
                                       valueId = null,
                                       valueName = null,
                                       disabled = false,
                                       placeholder = "Select…",
                                       onChange,
                                   }: Props) {
    const btnRef = useRef<HTMLButtonElement | null>(null);
    const listRef = useRef<HTMLUListElement | null>(null);
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState<number>(-1);

    const selected = useMemo<RoleOption | null>(() => {
        if (!options || options.length === 0) return null;
        if (valueId != null) {
            const hit = options.find((o) => o.id === valueId);
            if (hit) return hit;
        }
        if (valueName) {
            const hit = options.find(
                (o) => o.name.toLowerCase() === valueName.toLowerCase()
            );
            if (hit) return hit;
        }
        return null;
    }, [options, valueId, valueName]);

    useEffect(() => {
        if (!open) return;
        const onDocClick = (e: MouseEvent) => {
            const t = e.target as Node;
            if (btnRef.current?.contains(t)) return;
            if (listRef.current?.contains(t)) return;
            setOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [open]);

    const clampIndex = (i: number) =>
        Math.max(0, Math.min(options.length - 1, i));
    const moveActive = (delta: number) => {
        if (!options.length) return;
        setActive((prev) => clampIndex(prev < 0 ? 0 : prev + delta));
    };
    const commitActive = () => {
        if (active >= 0 && active < options.length) {
            onChange(options[active]);
            setOpen(false);
        }
    };

    return (
        <div className={`rs ${disabled ? "is-disabled" : ""}`}>
            <button
                type="button"
                ref={btnRef}
                className="rs-button"
                aria-haspopup="listbox"
                aria-expanded={open}
                disabled={disabled}
                onClick={() => {
                    if (disabled) return;
                    const nextOpen = !open;
                    setOpen(nextOpen);
                    if (nextOpen) {
                        const idx = selected
                            ? options.findIndex((o) => o.id === selected.id)
                            : -1;
                        setActive(idx >= 0 ? idx : 0);
                    }
                }}
                onKeyDown={(e) => {
                    if (disabled) return;
                    if (e.key === "ArrowDown") {
                        e.preventDefault();
                        if (!open) setOpen(true);
                        moveActive(1);
                    } else if (e.key === "ArrowUp") {
                        e.preventDefault();
                        if (!open) setOpen(true);
                        moveActive(-1);
                    } else if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (open) commitActive();
                        else setOpen(true);
                    } else if (e.key === "Escape") {
                        setOpen(false);
                    }
                }}
            >
        <span className="rs-left">
          {selected ? (
              <span className="rs-value">{formatRoleLabel(selected.name)}</span>
          ) : (
              <span className="rs-placeholder">{placeholder}</span>
          )}
        </span>

                <span className="rs-right">
          {selected && (
              <span
                  className={badgeClass(selected.name)}
                  aria-hidden="true"
              >
              {formatRoleLabel(selected.name)}
            </span>
          )}
                    <span className="rs-caret" aria-hidden>
            ▾
          </span>
        </span>
            </button>

            {open && (
                <ul
                    ref={listRef}
                    role="listbox"
                    className="rs-list"
                    tabIndex={-1}
                    onKeyDown={(e) => {
                        if (e.key === "ArrowDown") {
                            e.preventDefault();
                            moveActive(1);
                        } else if (e.key === "ArrowUp") {
                            e.preventDefault();
                            moveActive(-1);
                        } else if (e.key === "Enter") {
                            e.preventDefault();
                            commitActive();
                        } else if (e.key === "Escape") {
                            setOpen(false);
                        }
                    }}
                >
                    {options.map((opt, i) => {
                        const sel = selected?.id === opt.id;
                        return (
                            <li
                                key={opt.id}
                                role="option"
                                aria-selected={sel ? true : undefined}
                                className={`rs-option ${sel ? "is-selected" : ""} ${
                                    i === active ? "is-active" : ""
                                }`}
                                onMouseEnter={() => setActive(i)}
                                onMouseDown={(e: React.MouseEvent<HTMLLIElement>) => {
                                    e.preventDefault();
                                    onChange(opt);
                                    setOpen(false);
                                }}
                            >
                                <span className="rs-option-left">{formatRoleLabel(opt.name)}</span>
                                <span className={badgeClass(opt.name)}>{formatRoleLabel(opt.name)}</span>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
